import DateAndTime from 'date-and-time';

import { sortByKey, sortByKeyAsNumber } from 'utils/common';

/**
 * Processes quarterly financial data into chart data points.
 *
 * @param {object} oData - Single fiscal year financial data with quarterly details.
 * @param {boolean} bIsLastYear - Whether this is the most recent fiscal year.
 * @returns {array} Array of chart data points for the quarters.
 */
const fnProcessQuarterlyData = (oData, bIsLastYear) => {
  const aChronologicalQuarterlyData = sortByKey(
    oData.VALUE_OVER_TIME,
    'START_DATE',
    true
  );

  const aQuarterlyData = aChronologicalQuarterlyData.map(
    ({ BEGINNING_VALUE, START_DATE, DISTRIBUTIONS }) => {
      const oStartDate = DateAndTime.parse(START_DATE, 'YYYY-MM-DDTHH:mm:ss');
      const sFormattedStartDate = DateAndTime.format(oStartDate, 'MMM YYYY');
      return {
        name: sFormattedStartDate,
        fundValue: parseFloat(BEGINNING_VALUE),
        distributions: parseFloat(DISTRIBUTIONS),
      };
    }
  );

  // Include the ending value for the most recent quarter available
  if (bIsLastYear && aChronologicalQuarterlyData.length > 0) {
    const oLastQuarter =
      aChronologicalQuarterlyData[aChronologicalQuarterlyData.length - 1];
    const oEndDate = DateAndTime.parse(
      oLastQuarter.END_DATE,
      'YYYY-MM-DDTHH:mm:ss'
    );
    const sFormattedEndDate = DateAndTime.format(oEndDate, 'MMM YYYY');
    aQuarterlyData.push({
      name: sFormattedEndDate,
      fundValue: parseFloat(oLastQuarter.ENDING_VALUE),
      distributions: parseFloat(oLastQuarter.DISTRIBUTIONS),
    });
  }

  return aQuarterlyData;
};

/**
 * Processes annual financial data into chart data points.
 *
 * @param {object} oData - Single fiscal year financial data with only beginning/ending values.
 * @param {boolean} bIsLastYear - Whether this is the most recent fiscal year.
 * @returns {array} Array of chart data points for the year.
 */
const fnProcessAnnualData = (oData, bIsLastYear) => {
  const aAnnualData = [
    {
      name: `Jul ${oData.FISCALYEAR - 1}`,
      fundValue: parseFloat(oData.FISCAL_YEAR_BEGINNING_VALUE),
    },
  ];

  // Include the ending value for the most recent year available
  if (bIsLastYear) {
    aAnnualData.push({
      name: `Jun ${oData.FISCALYEAR}`,
      fundValue: parseFloat(oData.FISCAL_YEAR_ENDING_VALUE),
    });
  }

  return aAnnualData;
};

/**
 * Formats the fund financial data into a list of data points for the chart.
 *
 * @param {object} aData - All historical fund financial data.
 * @returns {array}
 */
export const fnFormatChartData = (aData) => {
  let aChartData = [];

  // Sort the data by fiscal year in ascending order
  const aChronologicalFinancialData = sortByKeyAsNumber(
    aData,
    'FISCALYEAR',
    true
  );

  aChronologicalFinancialData.forEach((oData, iIndex) => {
    const bIsLastAvailableYear =
      iIndex === aChronologicalFinancialData.length - 1;
    const bHasQuarterlyData =
      oData.VALUE_OVER_TIME && oData.VALUE_OVER_TIME.length > 0;

    if (bHasQuarterlyData) {
      const aQuarterlyData = fnProcessQuarterlyData(
        oData,
        bIsLastAvailableYear
      );
      aChartData = [...aChartData, ...aQuarterlyData];
    } else {
      const aAnnualData = fnProcessAnnualData(oData, bIsLastAvailableYear);
      aChartData = [...aChartData, ...aAnnualData];
    }
  });

  const aLastSixDataPoints = aChartData.slice(-6);
  return aLastSixDataPoints;
};

export default {};
